import { supabase } from "./supabase";
import type { User } from "../types/database";

// Portale Operatori endpoints
const PORTALE_AUTH_ME_COLLAUDO = "https://d19naaxe1q5j5l.cloudfront.net/api/auth/me";
const PORTALE_AUTH_ME_PROD = "https://portaleoperatori.italia.it/api/auth/me";

// Use collaudo by default, can be switched via env
const AUTH_ME_ENDPOINT = import.meta.env.VITE_PORTALE_ENV === "production"
  ? PORTALE_AUTH_ME_PROD
  : PORTALE_AUTH_ME_COLLAUDO;

export interface PortaleUser {
  id: number;
  ext_id: number;
  email: string;
  name: string;
  surname: string;
  cfPiva: string;
  isConsentAccepted: boolean;
  role: string;
  status: string;
  json?: {
    user?: {
      id: number;
      name: string;
      surname: string;
      email: string;
      fiscalCod: string;
      role: string;
      status: string;
    };
    activities?: Array<{
      id: string;
      identifier: string;
      businessName: string;
      operatorType: string;
      primaryAtecoCod: string;
      fiscalCod: string;
      vatNumber: string;
      address: string;
      civicNumber: string;
      city: string;
      province: string;
      zipCode: string;
      country: string;
      emailPEC: string;
      status: string;
      localUnits?: Array<{
        identifier: string;
        name: string;
        atecoCod: string;
        streetName: string;
        streetNumber: string;
        city: string;
        province: string;
        postalCode: string;
        region: string;
        country: string;
        mailAddress: string;
        status: string;
      }>;
    }>;
  };
  token: string;
  tokenCreatedAt: string;
  createdAt: string;
  updatedAt: string;
}

// Re-export error codes from authStore for consistency
export { AUTH_ERROR_CODES, AUTH_ERROR_MESSAGES } from "../stores/authStore";

// Extended error info for auth operations
export const AUTH_ERRORS = {
  TOKEN_EXPIRED: { code: "TOKEN_EXPIRED", message: "La sessione è scaduta. Effettua nuovamente l'accesso dal Portale Operatori." },
  USER_NOT_AUTHORIZED: { code: "USER_NOT_AUTHORIZED", message: "Non hai i permessi per accedere a questo servizio." },
  SYNC_FAILED: { code: "SYNC_FAILED", message: "Errore durante la sincronizzazione dell'utente." },
  INTERNAL_ERROR: { code: "INTERNAL_ERROR", message: "Si è verificato un errore imprevisto." },
} as const;

export interface AuthResult {
  success: boolean;
  user?: User;
  error?: string;
  errorCode?: string;
}

/**
 * Validates a JWT token from Portale Operatori
 * This should be called from a server/edge function for security
 * 
 * Conforme a Specifiche_Integrazione_TDH.md sezione "Validazione Token (GET Auth/me)"
 */
export async function validatePortaleJWT(jwt: string): Promise<{ user: PortaleUser | null; errorCode?: string }> {
  try {
    const response = await fetch(AUTH_ME_ENDPOINT, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${jwt}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      console.error("JWT validation failed:", response.status);
      // Per le specifiche, 401 = TOKEN_EXPIRED
      if (response.status === 401) {
        return { user: null, errorCode: AUTH_ERRORS.TOKEN_EXPIRED.code };
      }
      // 403 = USER_NOT_AUTHORIZED
      if (response.status === 403) {
        return { user: null, errorCode: AUTH_ERRORS.USER_NOT_AUTHORIZED.code };
      }
      return { user: null, errorCode: AUTH_ERRORS.INTERNAL_ERROR.code };
    }

    const userData: PortaleUser = await response.json();

    if (userData.status !== "ACTIVE") {
      console.error("User is not active:", userData.status);
      return { user: null, errorCode: AUTH_ERRORS.USER_NOT_AUTHORIZED.code };
    }

    return { user: userData };
  } catch (error) {
    console.error("Error validating JWT:", error);
    return { user: null, errorCode: AUTH_ERRORS.INTERNAL_ERROR.code };
  }
}

/**
 * Creates or updates a user in Supabase from Portale data
 */
export async function syncUserFromPortale(portaleUser: PortaleUser): Promise<User | null> {
  const userName = portaleUser.name || portaleUser.json?.user?.name || "";
  const userSurname = portaleUser.surname || portaleUser.json?.user?.surname || "";

  // Column names must match the database schema defined in 001_init.sql
  const userData = {
    portale_id: portaleUser.id,
    ext_id: portaleUser.ext_id,
    email: portaleUser.email,
    first_name: userName,
    last_name: userSurname,
    tax_id: portaleUser.cfPiva,
    role: portaleUser.role,
    status: portaleUser.status,
  };

  const { data, error } = await supabase
    .from("users")
    .upsert(userData, { onConflict: "portale_id" })
    .select()
    .single();

  if (error) {
    console.error("Error syncing user:", error);
    return null;
  }

  return data as User;
}

/**
 * Get user from Supabase by ID via edge function (more secure)
 * This uses an edge function that validates the session token
 */
export async function getUserById(userId: string, sessionToken: string): Promise<User | null> {
  try {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const response = await fetch(
      `${supabaseUrl}/functions/v1/get-user?token=${encodeURIComponent(sessionToken)}&user=${encodeURIComponent(userId)}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      console.error("Error fetching user:", response.status);
      return null;
    }

    const result = await response.json();
    if (!result.success || !result.user) {
      console.error("User not found or invalid response");
      return null;
    }

    return result.user as User;
  } catch (error) {
    console.error("Error fetching user:", error);
    return null;
  }
}

/**
 * Complete handshake flow: validate JWT and sync user
 * 
 * Conforme a Specifiche_Integrazione_TDH.md sezione "Autenticazione e Flusso JWT"
 */
export async function handlePortaleHandshake(jwt: string): Promise<AuthResult> {
  // 1. Validate JWT with Portale
  const validationResult = await validatePortaleJWT(jwt);
  if (!validationResult.user) {
    const errorCode = validationResult.errorCode || AUTH_ERRORS.TOKEN_EXPIRED.code;
    const errorInfo = Object.values(AUTH_ERRORS).find(e => e.code === errorCode) || AUTH_ERRORS.TOKEN_EXPIRED;
    return {
      success: false,
      error: errorInfo.message,
      errorCode: errorInfo.code,
    };
  }

  // 2. Sync user to Supabase
  const user = await syncUserFromPortale(validationResult.user);
  if (!user) {
    return {
      success: false,
      error: AUTH_ERRORS.SYNC_FAILED.message,
      errorCode: AUTH_ERRORS.SYNC_FAILED.code,
    };
  }

  return { success: true, user };
}

/**
 * Handle handshake session token (from redirect URL)
 * This is used when the user comes from Portale Operatori handshake flow
 */
export async function handleHandshakeSessionToken(
  sessionToken: string,
  userId: string
): Promise<AuthResult> {
  try {
    // User is already validated and synced by the handshake edge function
    // Retrieve the user data via secure edge function (validates token)
    const user = await getUserById(userId, sessionToken);

    if (!user) {
      return {
        success: false,
        error: "Utente non trovato",
        errorCode: AUTH_ERRORS.INTERNAL_ERROR.code,
      };
    }

    return { success: true, user };
  } catch (error) {
    console.error("Error handling session token:", error);
    return {
      success: false,
      error: "Errore durante l'autenticazione",
      errorCode: AUTH_ERRORS.INTERNAL_ERROR.code,
    };
  }
}

interface StoredSession {
  user: User;
  token: string;
  tokenExpiresAt?: number;
  lastActivity?: number;
}

/**
 * Get current session from localStorage
 */
export function getStoredSession(): StoredSession | null {
  try {
    const stored = localStorage.getItem("dpm_session");
    if (!stored) return null;
    return JSON.parse(stored);
  } catch {
    return null;
  }
}

/**
 * Store session in localStorage
 * 
 * Nota: Il Portale Operatori non supporta il refresh del token.
 * La sessione scade dopo 60 minuti (usiamo 55 min per sicurezza).
 */
export function storeSession(user: User, token: string, expiresIn?: number): void {
  const now = Date.now();
  const session: StoredSession = {
    user,
    token,
    tokenExpiresAt: expiresIn ? now + (expiresIn * 1000) : now + (55 * 60 * 1000),
    lastActivity: now,
  };
  localStorage.setItem("dpm_session", JSON.stringify(session));
}

/**
 * Update last activity timestamp
 */
export function updateSessionActivity(): void {
  try {
    const stored = getStoredSession();
    if (stored) {
      stored.lastActivity = Date.now();
      localStorage.setItem("dpm_session", JSON.stringify(stored));
    }
  } catch {
    // Ignore errors
  }
}

/**
 * Clear stored session
 */
export function clearSession(): void {
  localStorage.removeItem("dpm_session");
}
